import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Config as JoditConfig } from 'jodit/esm/config';
import type { Config as JoditPluginConfig } from 'jodit/config';
import { Jodit } from 'jodit';
import moment from 'moment';

/**
 * Normalizes a string by removing diacritical marks (accents).
 * Uses Unicode NFD normalization to decompose characters, then removes combining marks.
 * Example: "clinique de tèst" -> "clinique de test"
 */
export const normalizeString = (str: string): string =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Checks if a string contains another string, ignoring accents and case.
 * Example: "clinique de tèst".includes("test") would fail, but this function returns true.
 */
export const includesIgnoringAccents = (source: string, search: string): boolean =>
  normalizeString(source.toLowerCase()).includes(normalizeString(search.toLowerCase()));

export const addStyleToHead = (rel: string, href: string): void => {
  const head = document.getElementsByTagName('head')[0];

  const linkElement = document.createElement('link');
  linkElement.rel = rel;
  linkElement.href = href;

  head.appendChild(linkElement);
};

/**
 * Password strength validation regex.
 * Uses non-backtracking approach with explicit character classes to prevent ReDoS.
 * Requires at least one digit, one lowercase, one uppercase, and one special character.
 */
export const upperLowerSymbolNumberRegex = /(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])/;

// eslint-disable-next-line @typescript-eslint/ban-types
export const matchValidator = (matchTo: string): ValidatorFn => (control: AbstractControl): ValidationErrors | null => (
  control.parent?.value && control.value === (control.parent?.controls as any)[matchTo].value ? null : { matching: true }
);

/**
 * Valide une date au format français jj/mm/aaaa (parsing strict : rejette aussi
 * les dates sémantiquement invalides comme 31/02/2024 ou 99/99/9999).
 * La présence de la valeur reste portée par Validators.required.
 */
export const frenchDateValidator = (): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null;
  }
  return moment(control.value, 'DD/MM/YYYY', true).isValid() ? null : { frenchDate: true };
};

export const editorConfig = (height: number | string = '100%'): Partial<JoditConfig & JoditPluginConfig & { speechRecognize: { lang: string } }> => ({
  license: '13BFB-1NLH1-BWN5N-OO5LZ',
  spellcheck: true,
  autofocus: true,
  language: 'fr',
  toolbarAdaptive: false,
  toolbarSticky: true,
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  processPasteHTML: true,
  processPasteFromWord: true,
  inline: false,
  toolbarInlineForSelection: true,
  showPlaceholder: false,
  height,
  allowResizeX: false,
  allowResizeY: false,
  controls: {
    font: {
      list: Jodit.atom({
        '': 'Default',
        'Helvetica,sans-serif': 'Helvetica',
        'Arial,Helvetica,sans-serif': 'Arial',
        'Comic sans MS,sans-serif': 'Comic Sans MS',
        'Georgia,serif': 'Georgia',
        'Impact,Charcoal,sans-serif': 'Impact',
        'Tahoma,Geneva,sans-serif': 'Tahoma',
        'Times New Roman,Times,serif': 'Times New Roman',
        'Verdana,Geneva,sans-serif': 'Verdana'
      })
    }
  },
  colorPickerDefaultTab: 'color',
  speechRecognize: {
    lang: 'fr'
  },
  pageBreak: {
    separator: '<div style="display:block; clear:both; page-break-after:always;"></div>'
  },
  cursorAfterAutofocus: 'end',
  buttons: 'bold,italic,underline,strikethrough,eraser,ul,ol,font,fontsize,brush,paragraph,lineHeight,superscript,subscript,spellcheck,cut,copy,paste,selectall,image,hr,table,link,symbols,indent,outdent,left,center,right,justify,pageBreak,undo,redo,find,source,preview',
  disablePlugins: 'autocomplete',
  hidePoweredByJodit: true
});
